import { useState, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from '@repo/ui';
import { Checkbox } from '@repo/ui';
import { Badge } from '@repo/ui';
import { X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@repo/ui';
import { Button } from '@repo/ui';

interface Props {
    className?: string;
    name: string;
    label?: string;
    placeholder?: string;
    options?: { name: string; value: string }[];
    error?: string | null;
    disabled?: boolean;
    onChange?: (value: string[]) => void;
}

export default function MultiSelectInputForm({
    className,
    name,
    label,
    options = [],
    placeholder,
    error,
    disabled,
    onChange,
}: Props) {
    const { control, setValue, watch } = useFormContext();
    const [isOpen, setIsOpen] = useState(false);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const [triggerWidth, setTriggerWidth] = useState<string>('auto');
    const currentValue = watch(name);

    // Handle value as array or comma-separated string
    const selectedValues = currentValue
        ? Array.isArray(currentValue)
            ? currentValue
            : typeof currentValue === 'string'
            ? currentValue.split(',').filter(Boolean).map(String)
            : []
        : [];

    // Measure trigger width on open
    useEffect(() => {
        if (isOpen && triggerRef.current) {
            const width = triggerRef.current.getBoundingClientRect().width;
            setTriggerWidth(`${width}px`);
        }
    }, [isOpen]);

    const handleToggleOption = (value: string) => {
        const newSelectedValues = selectedValues.includes(value)
            ? selectedValues.filter((v) => v !== value)
            : [...selectedValues, value];
        
        // Store as array
        setValue(name, newSelectedValues, { shouldValidate: true });
        if (onChange) onChange(newSelectedValues);
    };

    const handleRemoveOption = (value: string, e: React.MouseEvent) => {
        e.stopPropagation();
        const newSelectedValues = selectedValues.filter((v) => v !== value);
        setValue(name, newSelectedValues, { shouldValidate: true });
        if (onChange) onChange(newSelectedValues);
    };

    const getDisplayText = () => {
        if (selectedValues.length === 0) return placeholder || 'Select options';
        if (selectedValues.length === 1) {
            const option = options.find((o) => o.value === selectedValues[0]);
            return option?.name || selectedValues[0];
        }
        return `${selectedValues.length} selected`;
    };

    return (
        <FormField
            control={control}
            name={name}
            render={({ field }) => (
                <FormItem className={className}>
                    <FormLabel className="text-primery-blue-900 font-medium md:text-[18px] text-[16px] leading-[33.73px] tracking-normal text-start">
                        {label}
                    </FormLabel>
                    <FormControl>
                        <Popover open={isOpen} onOpenChange={setIsOpen}>
                            <PopoverTrigger asChild>
                                <Button
                                    ref={triggerRef}
                                    variant="outline"
                                    className="w-full justify-between text-left font-normal"
                                    onClick={() => setIsOpen(!isOpen)}
                                    disabled={disabled}
                                >
                                    <span className="truncate">{getDisplayText()}</span>
                                    <span>▼</span>
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="p-0" style={{ width: triggerWidth }} align="start">
                                <div className="max-h-[200px] overflow-y-auto">
                                    {options.length === 0 ? (
                                        <p className="py-2 text-center text-sm text-muted-foreground">
                                            No options available
                                        </p>
                                    ) : (
                                        options.map((option) => (
                                            <div
                                                key={option.value}
                                                className="flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer"
                                                onClick={() => handleToggleOption(option.value)}
                                            >
                                                <Checkbox
                                                    checked={selectedValues.includes(option.value)}
                                                    onCheckedChange={() => handleToggleOption(option.value)}
                                                    className="mr-2"
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                                <span className="text-sm font-normal text-white-blue-900 font-cairo">
                                                    {option.name}
                                                </span>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </PopoverContent>
                        </Popover>
                    </FormControl>

                    {/* Display selected options as badges */}
                    {selectedValues.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                            {selectedValues.map((value) => {
                                const option = options.find((o) => o.value === value);
                                return (
                                    <Badge key={value} variant="outline" className="px-3 py-1 text-sm">
                                        {option?.name || value}
                                        <button
                                            type="button"
                                            onClick={(e) => handleRemoveOption(value, e)}
                                            className="ml-2 rounded-full outline-none focus:ring-2 focus:ring-primary"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </Badge>
                                );
                            })}
                        </div>
                    )}

                    <FormMessage>{error}</FormMessage>
                </FormItem>
            )}
        />
    );
}